import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { appointmentsAPI, clientsAPI } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { format } from "date-fns";
import { CalendarIcon, Loader2, Plus, User, CreditCard, AlertCircle, CheckCircle2, Copy, Mail, MessageSquare, Check } from "lucide-react";
import { cn, formatCurrency } from "@/lib/utils";

export default function NewAppointmentDialog({ 
    open, 
    onOpenChange, 
    clients, 
    services, 
    staff, 
    onSuccess,
    prefilledData = null
}) {
    const { user } = useAuth();
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [showNewClient, setShowNewClient] = useState(false);
    const [showPaymentLink, setShowPaymentLink] = useState(false);
    const [paymentInfo, setPaymentInfo] = useState(null);
    
    const [formData, setFormData] = useState({
        client_id: "",
        service_ids: [],
        staff_id: "any",
        date: new Date(),
        time: "09:00",
        notes: "",
        send_reminder: true,
        require_booking_fee: false,
        payment_method: "none",
    });
    
    const [newClient, setNewClient] = useState({
        name: "",
        phone: "",
        email: "",
    });

    const selectedServices = services.filter(s => formData.service_ids.includes(s.id));
    const selectedClient = clients.find(c => c.id === formData.client_id);
    
    // Calculate totals for selected services
    const totalPrice = selectedServices.reduce((sum, s) => sum + (s.price || 0), 0);
    const totalDuration = selectedServices.reduce((sum, s) => sum + (s.duration_minutes || 0), 0);
    
    // Calculate booking fee if enabled
    const bookingFeeEnabled = user?.booking_fee_enabled || false;
    const paystackEnabled = user?.paystack_enabled || false;
    const bookingFee = selectedServices.length > 0 && bookingFeeEnabled
        ? (user?.use_percentage_fee
            ? totalPrice * (user?.booking_fee_percentage / 100)
            : user?.booking_fee_amount || 0)
        : 0;

    // Group services by category
    const groupedServices = (() => {
        const groups = {};
        services.forEach(service => {
            const cat = service.category_name || service.category || 'Uncategorised';
            if (!groups[cat]) groups[cat] = [];
            groups[cat].push(service);
        });
        // Sort: named categories first, Uncategorised last
        const sorted = {};
        Object.keys(groups).sort((a, b) => {
            if (a === 'Uncategorised') return 1;
            if (b === 'Uncategorised') return -1;
            return a.localeCompare(b);
        }).forEach(key => { sorted[key] = groups[key]; });
        return sorted;
    })();

    // Prefill form when prefilledData changes
    useEffect(() => {
        if (prefilledData) {
            setFormData(prev => ({
                ...prev,
                date: prefilledData.date ? new Date(prefilledData.date) : prev.date,
                time: prefilledData.time ? prefilledData.time.substring(0, 5) : prev.time,
                staff_id: prefilledData.staff_id || "any"
            }));
        }
    }, [prefilledData]);

    // Reset form when dialog closes
    useEffect(() => {
        if (!open) {
            setStep(1);
            setShowPaymentLink(false);
            setPaymentInfo(null);
            if (!prefilledData) {
                resetForm();
            }
        }
    }, [open, prefilledData]);

    const toggleService = (serviceId) => {
        setFormData(prev => {
            const ids = prev.service_ids.includes(serviceId)
                ? prev.service_ids.filter(id => id !== serviceId)
                : [...prev.service_ids, serviceId];
            return { ...prev, service_ids: ids };
        });
    };

    const handleCreateClient = async () => {
        if (!newClient.name || !newClient.phone) {
            toast.error("Name and phone are required");
            return;
        }
        setLoading(true);
        try {
            const res = await clientsAPI.create(newClient);
            setFormData({ ...formData, client_id: res.data.id });
            onSuccess?.();
            setShowNewClient(false);
            setNewClient({ name: "", phone: "", email: "" });
            toast.success("Client added!");
        } catch (error) {
            toast.error("Failed to create client");
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async () => {
        if (!formData.client_id || formData.service_ids.length === 0 || !formData.date || !formData.time) {
            toast.error("Please fill in all required fields");
            return;
        }

        if (formData.require_booking_fee && formData.payment_method === "none") {
            toast.error("Please select a payment method");
            return;
        }

        if (formData.payment_method === "paystack" && !selectedClient?.email) {
            toast.error("Client email is required for online payments");
            return;
        }

        await createAppointment();
    };

    const createAppointment = async () => {
        setLoading(true);
        try {
            const appointmentData = {
                client_id: formData.client_id,
                service_ids: formData.service_ids,
                // Keep service_id for backward compat (primary service)
                service_id: formData.service_ids[0],
                staff_id: formData.staff_id === "any" ? undefined : formData.staff_id,
                date: format(formData.date, 'yyyy-MM-dd'),
                time: `${formData.time}:00`,
                notes: formData.notes,
                send_reminder: formData.send_reminder,
                booking_fee_required: formData.require_booking_fee && bookingFeeEnabled,
                booking_fee_paid: formData.payment_method === "cash",
                payment_method: formData.require_booking_fee ? formData.payment_method : undefined,
            };

            const response = await appointmentsAPI.create(appointmentData);
            
            if (response.data?.booking_fee_required && 
                response.data?.payment && 
                formData.payment_method === "paystack") {
                setPaymentInfo(response.data);
                setShowPaymentLink(true);
                toast.success("Appointment created! Share payment link with client.");
            } else if (formData.payment_method === "cash") {
                toast.success("Appointment booked! Cash payment will be collected in person.");
                onSuccess?.();
                onOpenChange(false);
                resetForm();
            } else {
                toast.success("Appointment booked successfully!");
                onSuccess?.();
                onOpenChange(false);
                resetForm();
            }
        } catch (error) {
            toast.error(error.response?.data?.error || "Failed to book appointment");
        } finally {
            setLoading(false);
        }
    };

    const copyPaymentLink = () => {
        if (paymentInfo?.payment?.authorization_url) {
            navigator.clipboard.writeText(paymentInfo.payment.authorization_url);
            toast.success("Payment link copied to clipboard!");
        }
    };

    const shareViaSMS = () => {
        if (!selectedClient?.phone || !paymentInfo?.payment?.authorization_url) return;
        const message = `Hi ${selectedClient.name}, please complete your booking payment (${formatCurrency(paymentInfo.booking_fee_amount)}): ${paymentInfo.payment.authorization_url}`;
        window.open(`sms:${selectedClient.phone}?body=${encodeURIComponent(message)}`);
    };

    const shareViaEmail = () => {
        if (!selectedClient?.email || !paymentInfo?.payment?.authorization_url) return;
        const subject = `Complete Your Booking - ${user?.business_name}`;
        const serviceNames = selectedServices.map(s => s.name).join(', ');
        const body = `Hi ${selectedClient.name},\n\nPlease complete your booking fee payment to confirm your appointment:\n\nServices: ${serviceNames}\nDate: ${format(formData.date, 'PPP')} at ${formData.time}\nBooking Fee: ${formatCurrency(paymentInfo.booking_fee_amount)}\n\nPayment Link: ${paymentInfo.payment.authorization_url}\n\nThank you!\n${user?.business_name}`;
        window.open(`mailto:${selectedClient.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`);
    };

    const resetForm = () => {
        setStep(1);
        setShowPaymentLink(false);
        setPaymentInfo(null);
        setFormData({
            client_id: "",
            service_ids: [],
            staff_id: "any",
            date: new Date(),
            time: "09:00",
            notes: "",
            send_reminder: true,
            require_booking_fee: false,
            payment_method: "none",
        });
    };

    const timeSlots = [];
    for (let h = 8; h <= 20; h++) {
        for (let m = 0; m < 60; m += 30) {
            const hour = h.toString().padStart(2, '0');
            const min = m.toString().padStart(2, '0');
            timeSlots.push(`${hour}:${min}`);
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[550px] max-h-[90vh] overflow-y-auto" data-testid="new-appointment-dialog">
                <DialogHeader>
                    <DialogTitle className="font-heading text-xl">
                        {showPaymentLink ? "Share Payment Link" : "Book Appointment"}
                    </DialogTitle>
                    <DialogDescription>
                        {showPaymentLink ? "Share this link with your client to complete payment" : `Step ${step} of 3`}
                    </DialogDescription>
                </DialogHeader>

                {/* Payment Link Screen */}
                {showPaymentLink && paymentInfo ? (
                    <div className="space-y-4">
                        <Alert className="border-green-200 bg-green-50">
                            <CheckCircle2 className="h-4 w-4 text-green-600" />
                            <AlertDescription className="text-green-800">
                                Appointment created with status: <strong>Pending Payment</strong>
                            </AlertDescription>
                        </Alert>

                        <div className="p-6 rounded-xl bg-accent/50 space-y-3">
                            <div className="flex justify-between">
                                <span className="text-muted-foreground">Client</span>
                                <strong>{selectedClient?.name}</strong>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-muted-foreground">Phone</span>
                                <strong>{selectedClient?.phone}</strong>
                            </div>
                            {selectedClient?.email && (
                                <div className="flex justify-between">
                                    <span className="text-muted-foreground">Email</span>
                                    <strong className="text-sm">{selectedClient.email}</strong>
                                </div>
                            )}
                            <div className="flex justify-between border-t pt-2">
                                <span className="text-muted-foreground">Services</span>
                                <strong className="text-right">{selectedServices.map(s => s.name).join(', ')}</strong>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-muted-foreground">Date & Time</span>
                                <strong>{format(formData.date, 'MMM d, yyyy')} at {formData.time}</strong>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-muted-foreground">Total Price</span>
                                <strong>{formatCurrency(totalPrice)}</strong>
                            </div>
                            <div className="flex justify-between pt-2 border-t">
                                <span className="text-muted-foreground">Booking Fee</span>
                                <strong className="text-primary text-lg">{formatCurrency(paymentInfo.booking_fee_amount)}</strong>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-muted-foreground">Status</span>
                                <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
                                    Pending Payment
                                </Badge>
                            </div>
                        </div>

                        <div className="space-y-3">
                            <Label>Payment Link</Label>
                            <div className="flex gap-2">
                                <Input 
                                    value={paymentInfo.payment?.authorization_url || ''} 
                                    readOnly 
                                    className="font-mono text-xs"
                                />
                                <Button variant="outline" size="icon" onClick={copyPaymentLink} title="Copy link">
                                    <Copy className="w-4 h-4" />
                                </Button>
                            </div>
                            <p className="text-xs text-muted-foreground">
                                Share this link with {selectedClient?.name} to complete the booking fee payment.
                            </p>
                        </div>

                        <Alert>
                            <AlertCircle className="h-4 w-4" />
                            <AlertDescription className="text-sm">
                                The appointment will remain <strong>Pending Payment</strong> until the client completes payment. 
                                Once paid, it will automatically update to <strong>Scheduled</strong>.
                            </AlertDescription>
                        </Alert>

                        <div className="space-y-2">
                            <Label>Share via:</Label>
                            <div className="grid grid-cols-2 gap-2">
                                <Button variant="outline" onClick={shareViaSMS} disabled={!selectedClient?.phone} className="w-full">
                                    <MessageSquare className="w-4 h-4 mr-2" /> SMS
                                </Button>
                                <Button variant="outline" onClick={shareViaEmail} disabled={!selectedClient?.email} className="w-full">
                                    <Mail className="w-4 h-4 mr-2" /> Email
                                </Button>
                            </div>
                        </div>

                        <div className="flex flex-col gap-2 pt-4">
                            <Button onClick={() => { copyPaymentLink(); onSuccess?.(); onOpenChange(false); resetForm(); }} className="w-full">
                                <Copy className="w-4 h-4 mr-2" /> Copy Link & Close
                            </Button>
                            <Button variant="outline" onClick={() => { onSuccess?.(); onOpenChange(false); resetForm(); }} className="w-full">
                                Done
                            </Button>
                        </div>
                    </div>
                ) : (
                    <>
                        {/* Progress indicator */}
                        <div className="flex gap-2 mb-4">
                            {[1, 2, 3].map((s) => (
                                <div key={s} className={cn("h-1 flex-1 rounded-full transition-colors", s <= step ? "bg-primary" : "bg-secondary")} />
                            ))}
                        </div>

                        {/* Step 1: Select Client */}
                        {step === 1 && (
                            <div className="space-y-4">
                                <div className="space-y-2">
                                    <Label>Select Client</Label>
                                    <Select value={formData.client_id} onValueChange={(v) => setFormData({ ...formData, client_id: v })}>
                                        <SelectTrigger data-testid="client-select">
                                            <SelectValue placeholder="Choose a client" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {clients.map((client) => (
                                                <SelectItem key={client.id} value={client.id}>
                                                    <div className="flex items-center gap-2">
                                                        <User className="w-4 h-4" />
                                                        {client.name} - {client.phone}
                                                    </div>
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className="relative">
                                    <div className="absolute inset-0 flex items-center"><span className="w-full border-t" /></div>
                                    <div className="relative flex justify-center text-xs uppercase">
                                        <span className="bg-background px-2 text-muted-foreground">or</span>
                                    </div>
                                </div>

                                {!showNewClient ? (
                                    <Button variant="outline" className="w-full" onClick={() => setShowNewClient(true)} data-testid="add-new-client-btn">
                                        <Plus className="w-4 h-4 mr-2" /> Add New Client
                                    </Button>
                                ) : (
                                    <div className="space-y-3 p-4 border rounded-xl">
                                        <Input placeholder="Client Name *" value={newClient.name} onChange={(e) => setNewClient({ ...newClient, name: e.target.value })} data-testid="new-client-name" />
                                        <Input placeholder="Phone Number *" value={newClient.phone} onChange={(e) => setNewClient({ ...newClient, phone: e.target.value })} data-testid="new-client-phone" />
                                        <Input placeholder="Email (optional)" type="email" value={newClient.email} onChange={(e) => setNewClient({ ...newClient, email: e.target.value })} />
                                        <div className="flex gap-2">
                                            <Button variant="outline" onClick={() => setShowNewClient(false)} className="flex-1">Cancel</Button>
                                            <Button onClick={handleCreateClient} disabled={loading} className="flex-1" data-testid="save-new-client-btn">
                                                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Client"}
                                            </Button>
                                        </div>
                                    </div>
                                )}

                                <div className="flex justify-end pt-4">
                                    <Button onClick={() => setStep(2)} disabled={!formData.client_id} data-testid="next-step-btn">Next</Button>
                                </div>
                            </div>
                        )}

                        {/* Step 2: Select Services & Payment Method */}
                        {step === 2 && (
                            <div className="space-y-4">
                                <div className="space-y-2">
                                    <div className="flex items-center justify-between">
                                        <Label>Select Services</Label>
                                        {formData.service_ids.length > 0 && (
                                            <Badge variant="secondary">{formData.service_ids.length} selected</Badge>
                                        )}
                                    </div>
                                    <p className="text-xs text-muted-foreground">Select one or more services for this appointment</p>
                                    <div className="max-h-[250px] overflow-y-auto space-y-4">
                                        {Object.entries(groupedServices).map(([category, categoryServices]) => (
                                            <div key={category}>
                                                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2 px-1">{category}</p>
                                                <div className="grid gap-2">
                                                    {categoryServices.map((service) => {
                                                        const isSelected = formData.service_ids.includes(service.id);
                                                        const serviceFee = bookingFeeEnabled
                                                            ? (user?.use_percentage_fee
                                                                ? service.price * (user?.booking_fee_percentage / 100)
                                                                : user?.booking_fee_amount || 0)
                                                            : 0;
                                                        return (
                                                            <div 
                                                                key={service.id}
                                                                onClick={() => toggleService(service.id)}
                                                                className={cn(
                                                                    "p-3 rounded-xl border-2 cursor-pointer transition-all",
                                                                    isSelected
                                                                        ? "border-primary bg-primary/5" 
                                                                        : "border-border hover:border-primary/50"
                                                                )}
                                                                data-testid={`service-${service.id}`}
                                                            >
                                                                <div className="flex justify-between items-center">
                                                                    <div className="flex items-center gap-3">
                                                                        <div className={cn(
                                                                            "w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 transition-colors",
                                                                            isSelected ? "bg-primary border-primary" : "border-muted-foreground/30"
                                                                        )}>
                                                                            {isSelected && <Check className="w-3 h-3 text-primary-foreground" />}
                                                                        </div>
                                                                        <div>
                                                                            <p className="font-medium text-sm">{service.name}</p>
                                                                            <p className="text-xs text-muted-foreground">{service.duration_minutes} mins</p>
                                                                        </div>
                                                                    </div>
                                                                    <p className="font-semibold text-primary text-sm">{formatCurrency(service.price)}</p>
                                                                </div>
                                                                {bookingFeeEnabled && (
                                                                    <div className="flex items-center gap-1 text-xs text-muted-foreground mt-2 pt-2 border-t ml-8">
                                                                        <CreditCard className="w-3 h-3" />
                                                                        Booking fee: {formatCurrency(serviceFee)}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Selected services summary */}
                                {selectedServices.length > 0 && (
                                    <div className="p-3 rounded-lg bg-accent/50 space-y-1">
                                        <div className="flex justify-between text-sm">
                                            <span className="text-muted-foreground">Total ({selectedServices.length} service{selectedServices.length > 1 ? 's' : ''})</span>
                                            <span className="font-bold text-primary">{formatCurrency(totalPrice)}</span>
                                        </div>
                                        <div className="flex justify-between text-xs text-muted-foreground">
                                            <span>Est. duration</span>
                                            <span>{totalDuration} mins</span>
                                        </div>
                                    </div>
                                )}

                                {/* Staff Selection */}
                                <div className="space-y-2">
                                    <Label>Assign Staff (optional)</Label>
                                    <Select value={formData.staff_id} onValueChange={(v) => setFormData({ ...formData, staff_id: v })}>
                                        <SelectTrigger>
                                            <SelectValue placeholder="Any available staff" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="any">Any Available</SelectItem>
                                            {staff.map((s) => (
                                                <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                {/* Booking Fee Section */}
                                {bookingFeeEnabled && selectedServices.length > 0 && (
                                    <div className="space-y-3">
                                        <div className="flex items-center justify-between p-3 rounded-xl bg-secondary/50">
                                            <div>
                                                <p className="font-medium text-sm">Require Booking Fee</p>
                                                <p className="text-xs text-muted-foreground">{formatCurrency(bookingFee)} booking fee</p>
                                            </div>
                                            <Switch checked={formData.require_booking_fee} onCheckedChange={(v) => setFormData({ ...formData, require_booking_fee: v, payment_method: v ? formData.payment_method : "none" })} />
                                        </div>
                                        
                                        {formData.require_booking_fee && (
                                            <div className="space-y-3 p-4 border rounded-xl">
                                                <Label>Payment Method</Label>
                                                <div className="grid grid-cols-2 gap-3">
                                                    <div onClick={() => setFormData({ ...formData, payment_method: "cash" })}
                                                        className={cn("relative p-4 rounded-lg border-2 cursor-pointer transition-all",
                                                            formData.payment_method === "cash" ? "border-primary bg-primary/5" : "border-border hover:border-primary/50")}>
                                                        <div className="flex flex-col items-center gap-2 text-center">
                                                            <div className={cn("w-12 h-12 rounded-full flex items-center justify-center", formData.payment_method === "cash" ? "bg-primary/10" : "bg-secondary")}>
                                                                <span className="text-2xl">💵</span>
                                                            </div>
                                                            <div><p className="font-semibold text-sm">Cash</p><p className="text-xs text-muted-foreground">Collect in person</p></div>
                                                        </div>
                                                        {formData.payment_method === "cash" && <CheckCircle2 className="w-5 h-5 text-primary absolute top-2 right-2" />}
                                                    </div>
                                                    <div onClick={() => paystackEnabled && setFormData({ ...formData, payment_method: "paystack" })}
                                                        className={cn("relative p-4 rounded-lg border-2 cursor-pointer transition-all",
                                                            formData.payment_method === "paystack" ? "border-primary bg-primary/5" : "border-border hover:border-primary/50",
                                                            !paystackEnabled && "opacity-50 cursor-not-allowed")}>
                                                        <div className="flex flex-col items-center gap-2 text-center">
                                                            <div className={cn("w-12 h-12 rounded-full flex items-center justify-center", formData.payment_method === "paystack" ? "bg-primary/10" : "bg-secondary")}>
                                                                <CreditCard className="w-6 h-6" />
                                                            </div>
                                                            <div><p className="font-semibold text-sm">Paystack</p><p className="text-xs text-muted-foreground">Online payment</p></div>
                                                        </div>
                                                        {formData.payment_method === "paystack" && <CheckCircle2 className="w-5 h-5 text-primary absolute top-2 right-2" />}
                                                    </div>
                                                </div>
                                                {!paystackEnabled && (
                                                    <Alert className="border-amber-200 bg-amber-50">
                                                        <AlertCircle className="h-4 w-4 text-amber-600" />
                                                        <AlertDescription className="text-xs text-amber-800">Paystack is not configured. Only cash payments available. Configure Paystack in Settings.</AlertDescription>
                                                    </Alert>
                                                )}
                                                {formData.payment_method === "cash" && (
                                                    <Alert className="border-green-200 bg-green-50">
                                                        <CheckCircle2 className="h-4 w-4 text-green-600" />
                                                        <AlertDescription className="text-sm text-green-800">
                                                            <strong>Cash Payment:</strong> Appointment will be confirmed immediately. 
                                                            Collect {formatCurrency(bookingFee)} from {selectedClient?.name} in person.
                                                        </AlertDescription>
                                                    </Alert>
                                                )}
                                                {formData.payment_method === "paystack" && paystackEnabled && (
                                                    <Alert className="border-blue-200 bg-blue-50">
                                                        <CreditCard className="h-4 w-4 text-blue-600" />
                                                        <AlertDescription className="text-sm text-blue-800">
                                                            <strong>Online Payment:</strong> A payment link will be generated. 
                                                            Share it with {selectedClient?.name} to complete the booking.
                                                            {!selectedClient?.email && (
                                                                <span className="block mt-1 text-amber-700 font-medium">⚠️ Client has no email. Add email or use SMS to share link.</span>
                                                            )}
                                                        </AlertDescription>
                                                    </Alert>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}

                                <div className="flex justify-between pt-4">
                                    <Button variant="outline" onClick={() => setStep(1)}>Back</Button>
                                    <Button onClick={() => setStep(3)} disabled={formData.service_ids.length === 0}>Next</Button>
                                </div>
                            </div>
                        )}

                        {/* Step 3: Date, Time & Confirmation */}
                        {step === 3 && (
                            <div className="space-y-4">
                                <div className="grid md:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Date</Label>
                                        <Popover>
                                            <PopoverTrigger asChild>
                                                <Button variant="outline" className="w-full justify-start" data-testid="date-picker">
                                                    <CalendarIcon className="w-4 h-4 mr-2" />
                                                    {format(formData.date, 'PPP')}
                                                </Button>
                                            </PopoverTrigger>
                                            <PopoverContent className="w-auto p-0">
                                                <Calendar mode="single" selected={formData.date} onSelect={(d) => d && setFormData({ ...formData, date: d })} disabled={(date) => date < new Date(new Date().setHours(0,0,0,0))} />
                                            </PopoverContent>
                                        </Popover>
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Time</Label>
                                        <Select value={formData.time} onValueChange={(v) => setFormData({ ...formData, time: v })}>
                                            <SelectTrigger data-testid="time-select"><SelectValue /></SelectTrigger>
                                            <SelectContent className="max-h-[200px]">
                                                {timeSlots.map((t) => (<SelectItem key={t} value={t}>{t}</SelectItem>))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label>Notes (optional)</Label>
                                    <Textarea placeholder="Any special requests or notes..." value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} />
                                </div>

                                <div className="flex items-center justify-between p-4 rounded-xl bg-secondary/50">
                                    <div>
                                        <p className="font-medium">Send Reminder</p>
                                        <p className="text-sm text-muted-foreground">Email/SMS 24 hours before</p>
                                    </div>
                                    <Switch checked={formData.send_reminder} onCheckedChange={(v) => setFormData({ ...formData, send_reminder: v })} />
                                </div>

                                {/* Summary */}
                                <div className="p-4 rounded-xl bg-accent/50 space-y-2">
                                    <p className="text-sm font-medium text-muted-foreground">Appointment Summary</p>
                                    <div className="space-y-1">
                                        <div className="flex justify-between">
                                            <span>Client:</span>
                                            <strong>{selectedClient?.name}</strong>
                                        </div>
                                        <div className="flex justify-between items-start">
                                            <span>Services:</span>
                                            <div className="text-right">
                                                {selectedServices.map((s, i) => (
                                                    <div key={s.id} className="text-sm">
                                                        <strong>{s.name}</strong>
                                                        <span className="text-muted-foreground ml-1">({formatCurrency(s.price)})</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                        <div className="flex justify-between border-t pt-1">
                                            <span>Total Price:</span>
                                            <strong>{formatCurrency(totalPrice)}</strong>
                                        </div>
                                        <div className="flex justify-between">
                                            <span>Est. Duration:</span>
                                            <strong>{totalDuration} mins</strong>
                                        </div>
                                        {formData.require_booking_fee && bookingFeeEnabled && (
                                            <>
                                                <div className="flex justify-between text-primary">
                                                    <span>Booking Fee:</span>
                                                    <strong>{formatCurrency(bookingFee)}</strong>
                                                </div>
                                                <div className="flex justify-between items-center">
                                                    <span>Payment Method:</span>
                                                    <Badge variant={formData.payment_method === "cash" ? "default" : "outline"}>
                                                        {formData.payment_method === "cash" ? "💵 Cash" : "💳 Paystack"}
                                                    </Badge>
                                                </div>
                                            </>
                                        )}
                                        <div className="flex justify-between pt-2 border-t">
                                            <span>When:</span>
                                            <strong>{format(formData.date, 'PPP')} at {formData.time}</strong>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex justify-between pt-4">
                                    <Button variant="outline" onClick={() => setStep(2)}>Back</Button>
                                    <Button onClick={handleSubmit} disabled={loading} data-testid="book-appointment-btn">
                                        {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                                        {formData.require_booking_fee && formData.payment_method === "paystack" 
                                            ? "Create & Get Payment Link" 
                                            : formData.require_booking_fee && formData.payment_method === "cash"
                                            ? "Book & Record Cash Payment"
                                            : "Book Appointment"}
                                    </Button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}
